/**
 * Integration with Podscribe (https://podscribe.com), the podcast attribution vendor used by the
 * Right Side Up media agency. index.html loads the tag once OneTrust targeting consent (C0004) is
 * granted and fires the visit event; this module reports purchases.
 *
 * @see https://app.podscribe.com/pixel-code/fi
 */
import analytics, { getTrackedProductsValue, shouldTrackMarketingEvent } from '.';
import { getTenancy } from '../../hooks/useTenancy';
import { typedWindow } from '../Window';
import { ECOM_EVENT_ORDER_COMPLETED, SegmentPurchaseEventProperties } from './events';

type PodscribePurchase = {
  value: number;
  order_number: string;
  currency: string;
  discount_code?: string;
};

/** Non-test production only, matching the other marketing pixels. */
function podscribeEnabled(): boolean {
  return getTenancy() !== 'test' && process.env.REACT_APP_ENVIRONMENT === 'production';
}

/**
 * Builds the purchase payload. Value is the tracked collar + subscription figure the other
 * marketing pixels report, so podscribe roas stays comparable to agentio, vibe, and northbeam.
 */
export function podscribePurchasePayload(properties: SegmentPurchaseEventProperties): PodscribePurchase {
  const purchase: PodscribePurchase = {
    value: getTrackedProductsValue(properties),
    order_number: properties.orderId,
    currency: properties.currency,
  };

  // podscribe raises a data issue for a purchase carrying an empty discount code, so the key is
  // only sent when the order actually used one.
  if (properties.code) {
    purchase.discount_code = properties.code;
  }

  return purchase;
}

function podscribeOrderCompleted(_messageId: string, properties: SegmentPurchaseEventProperties) {
  if (!podscribeEnabled()) {
    return;
  }

  // fiPodscribe queues until the tag has loaded and initialized, so the confirmation page cannot
  // lose this event to the script still being in flight.
  typedWindow.fiPodscribe?.('purchase', podscribePurchasePayload(properties));
}

// Map Segment events to Podscribe handlers
type PodscribeEventHandler = (messageId: string, properties: any) => void;
const PODSCRIBE_EVENT_MAP: Record<string, PodscribeEventHandler> = {
  [ECOM_EVENT_ORDER_COMPLETED]: podscribeOrderCompleted,
};

function onTrack(event: string, properties: any, messageId: string) {
  const handler = PODSCRIBE_EVENT_MAP[event];
  if (!handler || !shouldTrackMarketingEvent(properties)) {
    return;
  }
  handler(messageId, properties);
}

/** Wire Order Completed to the podscribe purchase event; safe to call pre-consent. */
export function podscribeSetupCallbacks() {
  analytics.onTrack(onTrack);
}
